/**
 * @fileoverview Document-type picker shown inside the canvas builder when creating a new template.
 * @module ui-admin/components/DocumentTypeSelector
 *
 * Responsibilities: - Present the available document types (Inspection Report, Permit, Custom) as
 *                     large clickable cards so the builder can shape the palette and data bindings.
 * Dependencies:     - React, lucide-react icons.
 * Architecture:     - Pure presentational; no data access. Caller stores the chosen type on the
 *                     template config.
 * Notes:            - Used by both tenant-web (DocumentTemplateBuilderPage) and system-web
 *                     (SystemDocumentBuilderPage) when isNew && method === 'scratch'.
 *
 * @component DocumentTypeSelector
 * Purpose:     Full-page overlay rendered inside the builder before any canvas editing begins.
 *              Prompts the user to choose the document type for the new template.
 * Props:       onSelect, onBack?
 * Behavior:    Renders three type cards; clicking one calls onSelect(type) and the builder
 *              dismisses the overlay and opens the canvas.
 * Data Source: none
 */
import React from 'react';
import { ClipboardList, ShieldCheck, Layers, ArrowLeft } from 'lucide-react';

export type DocumentTypeSelectorValue = 'inspectionReport' | 'permit' | 'custom';

export interface DocumentTypeSelectorProps {
  /** Called when the user selects a document type. */
  onSelect: (type: DocumentTypeSelectorValue) => void;
  /** Optional back handler (returns to the creation-method screen). */
  onBack?: () => void;
}

interface TypeCard {
  type: DocumentTypeSelectorValue;
  icon: React.ReactNode;
  title: string;
  description: string;
  tags: string[];
  accent: string;
  iconBg: string;
  border: string;
  hoverBorder: string;
}

/**
 * @component DocumentTypeSelector
 */
export default function DocumentTypeSelector({ onSelect, onBack }: DocumentTypeSelectorProps) {
  const cards: TypeCard[] = [
    {
      type: 'inspectionReport',
      icon: <ClipboardList className="h-9 w-9" />,
      title: 'Inspection Report',
      description:
        'A structured report capturing violations, photo evidence, form responses, inspector notes, and compliance status.',
      tags: ['Violations list', 'Photos', 'Form responses', 'Signatures'],
      accent: 'text-blue-600',
      iconBg: 'bg-blue-50',
      border: 'border-gray-200',
      hoverBorder: 'hover:border-blue-400 hover:shadow-blue-100',
    },
    {
      type: 'permit',
      icon: <ShieldCheck className="h-9 w-9" />,
      title: 'Permit',
      description:
        'A permit or certificate with conditions, recipient information, legal text, and approval signatures.',
      tags: ['Permit info', 'Conditions', 'Recipient block', 'Signatures'],
      accent: 'text-green-600',
      iconBg: 'bg-green-50',
      border: 'border-gray-200',
      hoverBorder: 'hover:border-green-400 hover:shadow-green-100',
    },
    {
      type: 'custom',
      icon: <Layers className="h-9 w-9" />,
      title: 'Custom Document',
      description:
        'A letter, notice of violation, certificate of compliance, or any other document type — full canvas freedom.',
      tags: ['Any layout', 'Custom fields', 'Merge fields', 'Cover letter'],
      accent: 'text-orange-600',
      iconBg: 'bg-orange-50',
      border: 'border-gray-200',
      hoverBorder: 'hover:border-orange-400 hover:shadow-orange-100',
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-120px)] p-8">
      <div className="w-full max-w-3xl">
        {/* Back link */}
        {onBack && (
          <button
            onClick={onBack}
            className="mb-6 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        )}

        {/* Heading */}
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            What kind of document are you creating?
          </h2>
          <p className="mt-2 text-gray-500 text-sm">
            The type shapes the data palette — you can change it any time from the template settings.
          </p>
        </div>

        {/* Type cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {cards.map((card) => (
            <button
              key={card.type}
              onClick={() => onSelect(card.type)}
              className={`
                group flex flex-col items-start gap-4 w-full text-left
                rounded-2xl border-2 ${card.border} ${card.hoverBorder}
                p-6 transition-all duration-150
                hover:shadow-lg focus:outline-hidden focus-visible:ring-2 focus-visible:ring-orange-400
                bg-white
              `}
            >
              {/* Icon */}
              <span className={`rounded-xl p-3 ${card.iconBg} ${card.accent}`}>
                {card.icon}
              </span>

              {/* Text */}
              <span className="flex flex-col gap-1.5">
                <span className={`text-base font-semibold text-gray-900 group-hover:${card.accent} transition-colors`}>
                  {card.title}
                </span>
                <span className="text-sm text-gray-500 leading-relaxed">
                  {card.description}
                </span>
              </span>

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5 mt-auto">
                {card.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-gray-100 text-gray-600"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </button>
          ))}
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          Not sure? Choose <span className="font-medium">Custom Document</span> — you can always add any component from the palette.
        </p>
      </div>
    </div>
  );
}
